import type { Placement } from '@floating-ui/react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react'
import classNames from 'classnames'
import { Spinner } from 'components/Spinner'
import {
  BreakoutButton,
  BreakoutIconButton,
  type ButtonKind,
} from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useUniqueElement } from 'hooks/uniqueElements'
import { observer } from 'mobx-react-lite'
import React, { useState, forwardRef, useEffect, useId } from 'react'

type Props = React.HtmlHTMLAttributes<HTMLDivElement>

export const FloatingIconActionButton = observer(function FloatingActionButton({
  className,
  label,
  size,
  buttonClassName,
  actions,
  Icon,
  kind,
  menuBottom,
  placement = menuBottom ? 'bottom-end' : 'top-end',
  'aria-label': ariaLabel,
  dropdownClassName,
  ...rest
}: Props & {
  Icon: React.ComponentType<{ size: number }>
  label?: string
  actions: React.ReactNode | Array<MenuButtonProps>
  kind?: ButtonKind
  size?: 'small' | 'large'
  buttonClassName?: string
  'aria-label'?: string
  dropdownClassName?: string
  menuBottom?: boolean
  placement?: Placement
}) {
  const [menuVisible, setMenuVisible] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open: menuVisible,
    onOpenChange: setMenuVisible,
    middleware: [offset(8), flip(), shift()],
    placement: placement,
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ])

  const id = useId()
  const uniqueElement = useUniqueElement('floating-action-button', id, () => {
    setMenuVisible(false)
  })

  useEffect(() => {
    if (menuVisible) uniqueElement.mount()
  }, [menuVisible, uniqueElement])

  return (
    <div {...rest} className={classNames(className)}>
      {!label && (
        <div ref={refs.setReference} {...getReferenceProps()}>
          <BreakoutIconButton
            kind={kind}
            aria-label={ariaLabel}
            size={size}
            aria-haspopup="true"
            aria-expanded={menuVisible}
            icon={<Icon aria-hidden size={18} />}
            className={classNames(
              'rounded-full border-none shadow-sm',
              buttonClassName
            )}
            disabled={Array.isArray(actions) && actions.length === 0}
          />
        </div>
      )}
      {label && (
        <div ref={refs.setReference} {...getReferenceProps()}>
          <BreakoutButton
            kind={kind}
            aria-label={ariaLabel}
            size={size}
            aria-haspopup="true"
            aria-expanded={menuVisible}
            icon={<Icon aria-hidden size={15} />}
            className={classNames(buttonClassName)}
            disabled={Array.isArray(actions) && actions.length === 0}
          >
            {label}
          </BreakoutButton>
        </div>
      )}
      {menuVisible && (
        <FloatingPortal>
          <RenderChildren
            ref={refs.setFloating}
            style={floatingStyles}
            dropdownClassName={dropdownClassName}
            {...getFloatingProps()}
          >
            {actions}
          </RenderChildren>
        </FloatingPortal>
      )}
    </div>
  )
})

export type MenuButtonProps = {
  Icon: React.ComponentType<{ size: number; className?: string }>
  text?: string | React.ReactNode
  onClick: () => void
  isLoading?: boolean
  disabled?: boolean
  tooltip?: string
  hide?: boolean
  tooltipPlacement?: Placement
  className?: string
}

const MenuButton = ({
  Icon,
  text,
  tooltip = '',
  onClick,
  isLoading,
  disabled,
  tooltipPlacement,
  className,
}: MenuButtonProps) => {
  return (
    <BreakoutTooltip
      content={tooltip}
      enabled={!!tooltip}
      placement={tooltipPlacement}
    >
      <div className="w-full">
        <button
          onClick={!isLoading ? onClick : undefined}
          disabled={disabled || isLoading}
          className={classNames(
            'flex w-full flex-row items-center gap-1 rounded-lg px-4 py-2 hover:bg-core-tertiary',
            disabled ? 'text-on-surface-disabled' : '',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            className
          )}
        >
          {isLoading ? (
            <Spinner aria-hidden size={0.938} /> // 15px as rem
          ) : disabled ? (
            <Icon aria-hidden size={15} className="text-on-surface-disabled" />
          ) : (
            <Icon aria-hidden size={15} />
          )}
          <strong className="text-label-small">{text}</strong>
        </button>
      </div>
    </BreakoutTooltip>
  )
}

type RenderChildrenProps = {
  children: React.ReactNode | Array<MenuButtonProps>
  dropdownClassName?: string
  style?: React.CSSProperties
}

const RenderChildren = forwardRef<HTMLDivElement, RenderChildrenProps>(
  ({ children, dropdownClassName, style }, ref) => {
    if (!Array.isArray(children)) {
      return (
        <div
          ref={ref}
          style={style}
          className={classNames('z-20', dropdownClassName)}
        >
          {children}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        style={style}
        role="menu"
        className={classNames(
          `z-20 flex flex-col gap-1 text-nowrap rounded-[16px] bg-surface px-4 py-4 shadow-3xl-floating`,
          dropdownClassName
        )}
      >
        {children.map((child, index) => {
          if (child.hide === true) return null
          return <MenuButton key={index} {...child} />
        })}
      </div>
    )
  }
)
RenderChildren.displayName = 'RenderChildren'
